import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Grid, Paper, TextField, InputAdornment, CircularProgress, Typography, IconButton, Autocomplete } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SearchUtils from "../utils/SearchUtils";

const SCAN_DEBOUNCE_MS = 300;
const BIN_DEBOUNCE_MS = 800; // Delay before auto-validating and jumping to Save

const ScanSection = ({
  t,
  prescriptionAssign,
  rxNumber,
  scanInputResetKey,
  onScanChange,
  scanRxInputRef,
  binId,
  onBinIdChange,
  onLocationBlur,
  onLocationKeyPress,
  onLocationSelect,
  onLocationOptionsOpen,
  locationOptions = [],
  isLoadingLocationOptions = false,
  locationError,
  isValidatingLocation,
  validatedLocation,
  enableTrim,
  trimChars,
  isEpicHasValidRx,
  isEpicHasValidRxMessage,
  isNonRxDeliverEnabled,
  otcDeliveryEntitlementLoading,
  showNonRxScan,
  upcScan,
  upcScanBusy,
  onUpcScanChange,
  onUpcScanKeyDown,
  onUpcScanSubmit,
  showRoomNumber,
  showLocationDropdown: showLocationDropdownProp,
  showOrderNotes: showOrderNotesProp,
  roomNumber,
  onRoomNumberChange,
  orderNotes,
  onOrderNotesChange,
}) => {
  const [localRxNumber, setLocalRxNumber] = useState(rxNumber || "");
  const [localBinId, setLocalBinId] = useState(binId || "");
  const [localRoomNumber, setLocalRoomNumber] = useState(roomNumber || "");
  const [localOrderNotes, setLocalOrderNotes] = useState(orderNotes || "");
  const debounceTimerRef = useRef(null);
  const binDebounceTimerRef = useRef(null);
  const latestValueRef = useRef(rxNumber || "");

  // Sync local value when parent clears or sets rxNumber (e.g. after scan success)
  useEffect(() => {
    setLocalRxNumber(rxNumber || "");
    latestValueRef.current = rxNumber || "";
  }, [rxNumber]);

  useEffect(() => {
    setLocalRxNumber("");
    latestValueRef.current = "";
  }, [scanInputResetKey]);

  // Sync local value when parent sets/clears binId
  useEffect(() => {
    setLocalBinId(binId || "");
    if (!binId && binDebounceTimerRef.current) {
      clearTimeout(binDebounceTimerRef.current);
      binDebounceTimerRef.current = null;
    }
  }, [binId]);

  useEffect(() => {
    setLocalRoomNumber(roomNumber || "");
  }, [roomNumber]);

  useEffect(() => {
    setLocalOrderNotes(orderNotes || "");
  }, [orderNotes]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (binDebounceTimerRef.current) {
        clearTimeout(binDebounceTimerRef.current);
      }
    };
  }, []);

  const handleScanChange = (value) => {
    setLocalRxNumber(value);
    latestValueRef.current = value;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      onScanChange(value);
    }, SCAN_DEBOUNCE_MS);
  };

  const handleBinIdChange = (rawValue) => {
    const trimmedValue = enableTrim
      ? SearchUtils.trimLeadingTrailing(rawValue, trimChars)
      : rawValue.trim();

    setLocalBinId(rawValue); // Update visual field immediately

    if (onBinIdChange) {
      onBinIdChange(trimmedValue); // Update parent state immediately
    }

    // Clear existing timer
    if (binDebounceTimerRef.current) {
      clearTimeout(binDebounceTimerRef.current);
      binDebounceTimerRef.current = null;
    }

    // Only start timer if we have content
    if (trimmedValue) {
      binDebounceTimerRef.current = setTimeout(() => {
        binDebounceTimerRef.current = null;
        // Trigger validation and focus jump (re-using onLocationBlur logic)
        if (onLocationBlur) {
          onLocationBlur();
        }
      }, BIN_DEBOUNCE_MS);
    }
  };

  const handleRoomNumberChange = (value) => {
    setLocalRoomNumber(value);
    if (onRoomNumberChange) {
      onRoomNumberChange(value);
    }
  };

  const handleOrderNotesChange = (value) => {
    setLocalOrderNotes(value);
    if (onOrderNotesChange) {
      onOrderNotesChange(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Clear any pending debounce and fire immediately
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      // Use ref to avoid stale closure - always has the latest typed value
      const currentValue = latestValueRef.current;
      if (currentValue && currentValue.trim().length > 0) {
        onScanChange(currentValue);
      }
    }
  };

  const getLocationOptionLabel = (option) => {
    if (!option) return "";
    if (typeof option === "string") return option;
    const label = option.location || "";
    const code = option.location_code || "";
    return code && code !== label ? `${label} (${code})` : label;
  };

  const selectedLocationOption = (locationOptions || []).find((option) => {
    const normalizedBin = (localBinId || "").trim().toLowerCase();
    return normalizedBin && (
      String(option.location || "").trim().toLowerCase() === normalizedBin ||
      String(option.location_code || "").trim().toLowerCase() === normalizedBin
    );
  }) || null;

  const showLocationDropdown =
    showLocationDropdownProp !== undefined
      ? !!showLocationDropdownProp
      : !!showRoomNumber;
  const showOrderNotes =
    showOrderNotesProp !== undefined
      ? !!showOrderNotesProp
      : !!showRoomNumber;
  const showUpcField = showNonRxScan === true || isNonRxDeliverEnabled;
  const scanFieldWidth = showUpcField ? 4 : 6;
  const upcFieldWidth = 4;
  const locationFieldWidth = showUpcField ? 4 : 6;
  const roomFieldWidth = 4;

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        mb: 3,
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        border: "1px solid #e2e8f0",
        borderRadius: 3,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      }}
    >
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={scanFieldWidth}>
          <TextField
            inputRef={scanRxInputRef}
            variant="outlined"
            placeholder={t("Assign.ScanRxNow") || "Scan RX Now"}
            value={localRxNumber}
            onChange={(e) => handleScanChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!prescriptionAssign}
            fullWidth
            InputProps={{
              startAdornment: (
                <Box
                  sx={{
                    mr: 1.5,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <SearchIcon
                    sx={{
                      color: prescriptionAssign ? "#7c3aed" : "#b0bec5",
                      fontSize: 24,
                    }}
                  />
                </Box>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#ffffff",
                transition: "all 0.3s ease",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e2e8f0",
                  borderWidth: 2,
                },
                "&:hover": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#7c3aed",
                  },
                },
                "&.Mui-focused": {
                  boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.1)",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#7c3aed",
                    borderWidth: 2,
                  },
                },
              },
            }}
          />
        </Grid>
        {showUpcField ? (
          <Grid item xs={12} sm={upcFieldWidth}>
            <TextField
              id="marketplace-upc-scan"
              variant="outlined"
              placeholder={t("Assign.ScanNonRxUpc", {
                defaultValue: "Scan or enter Marketplace UPC",
              })}
              value={upcScan || ""}
              onChange={onUpcScanChange}
              onKeyDown={onUpcScanKeyDown}
              disabled={
                otcDeliveryEntitlementLoading ||
                !isNonRxDeliverEnabled ||
                !prescriptionAssign ||
                upcScanBusy
              }
              helperText={
                otcDeliveryEntitlementLoading
                  ? t("Assign.ScanNonRxUpcLoading", {
                      defaultValue: "Checking Marketplace availability…",
                    })
                  : isNonRxDeliverEnabled
                    ? t("Assign.ScanNonRxUpcHint", {
                        defaultValue:
                          "Type or scan a UPC, then press Enter to add it to the cart.",
                      })
                    : t("Assign.ScanNonRxUpcUnavailable", {
                        defaultValue: "Marketplace is not available yet.",
                      })
              }
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      mr: 1.5,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <SearchIcon
                      sx={{
                        color: prescriptionAssign ? "#00a7e1" : "#b0bec5",
                        fontSize: 24,
                      }}
                    />
                  </Box>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label={t("Assign.ScanNonRxUpcAdd", {
                        defaultValue: "Add Marketplace item",
                      })}
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      onClick={onUpcScanSubmit}
                      disabled={
                        otcDeliveryEntitlementLoading ||
                        !isNonRxDeliverEnabled ||
                        !prescriptionAssign ||
                        upcScanBusy ||
                        !(upcScan || "").trim()
                      }
                    >
                      <AddCircleOutlineRounded fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e2e8f0",
                    borderWidth: 2,
                  },
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a7e1",
                    },
                  },
                  "&.Mui-focused": {
                    boxShadow: "0 0 0 3px rgba(0, 167, 225, 0.12)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a7e1",
                      borderWidth: 2,
                    },
                  },
                },
              }}
            />
          </Grid>
        ) : null}
        <Grid item xs={12} sm={locationFieldWidth} sx={{ position: "relative", pb: locationError ? 2.75 : 0 }}>
          {showLocationDropdown ? (
            <Autocomplete
              options={locationOptions}
              value={selectedLocationOption}
              inputValue={localBinId}
              openOnFocus
              onOpen={() => {
                if (onLocationOptionsOpen) onLocationOptionsOpen();
              }}
              onInputChange={(event, value, reason) => {
                if (reason === "input") {
                  handleBinIdChange(value || "");
                } else if (reason === "clear") {
                  setLocalBinId("");
                  if (onLocationSelect) onLocationSelect(null);
                }
              }}
              onChange={(event, option) => {
                if (binDebounceTimerRef.current) {
                  clearTimeout(binDebounceTimerRef.current);
                  binDebounceTimerRef.current = null;
                }
                if (option) {
                  setLocalBinId(option.location || "");
                  if (onLocationSelect) onLocationSelect(option);
                } else {
                  setLocalBinId("");
                  if (onLocationSelect) onLocationSelect(null);
                }
              }}
              getOptionLabel={getLocationOptionLabel}
              isOptionEqualToValue={(option, value) =>
                option && value && String(option.id) === String(value.id)
              }
              filterOptions={(options, state) => {
                const query = (state.inputValue || "").trim().toLowerCase();
                if (!query) return options;
                const selectedLocation = selectedLocationOption
                  ? String(selectedLocationOption.location || "").trim().toLowerCase()
                  : "";
                const selectedCode = selectedLocationOption
                  ? String(selectedLocationOption.location_code || "").trim().toLowerCase()
                  : "";
                // The selected/default location is a value, not an active search.
                if (selectedLocationOption && (query === selectedLocation || query === selectedCode)) {
                  return options;
                }
                return options.filter((option) =>
                  String(option.location || "").toLowerCase().includes(query) ||
                  String(option.location_code || "").toLowerCase().includes(query)
                );
              }}
              freeSolo
              loading={isLoadingLocationOptions}
              noOptionsText={t("Assign.NoLocationsFound", { defaultValue: "No mapped locations found" })}
              disabled={!prescriptionAssign}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={t("Assign.Location") || "Location"}
                  onBlur={onLocationBlur}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      if (binDebounceTimerRef.current) {
                        clearTimeout(binDebounceTimerRef.current);
                        binDebounceTimerRef.current = null;
                      }
                      if (onLocationKeyPress) onLocationKeyPress(e);
                    } else if (onLocationKeyPress) {
                      onLocationKeyPress(e);
                    }
                  }}
                  required
                  error={Boolean(locationError)}
                  helperText={locationError || ""}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isValidatingLocation || isLoadingLocationOptions ? (
                          <CircularProgress size={16} sx={{ color: "#7c3aed" }} />
                        ) : validatedLocation ? (
                          <CheckCircleIcon sx={{ color: "#10b981", fontSize: 20 }} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#ffffff",
                      transition: "all 0.3s ease",
                      fontSize: "0.875rem",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: locationError
                          ? "#ef4444"
                          : validatedLocation
                            ? "#10b981"
                            : "#e2e8f0",
                        borderWidth: 2,
                      },
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: locationError
                            ? "#ef4444"
                            : validatedLocation
                              ? "#10b981"
                              : "#7c3aed",
                        },
                      },
                      "&.Mui-focused": {
                        boxShadow: locationError
                          ? "0 0 0 3px rgba(239, 68, 68, 0.1)"
                          : validatedLocation
                            ? "0 0 0 3px rgba(16, 185, 129, 0.1)"
                            : "0 0 0 3px rgba(124, 58, 237, 0.1)",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: locationError
                            ? "#ef4444"
                            : validatedLocation
                              ? "#10b981"
                              : "#7c3aed",
                          borderWidth: 2,
                        },
                      },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "0.875rem",
                      color: "#9e9e9e",
                      opacity: 1,
                    },
                    "& .MuiFormHelperText-root": {
                      fontSize: "0.75rem",
                      marginTop: "4px",
                      marginLeft: 0,
                      position: "absolute",
                      top: "100%",
                    },
                  }}
                />
              )}
            />
          ) : (
            <TextField
            variant="outlined"
            placeholder={t("Assign.Location") || "Location"}
            value={localBinId}
            onChange={(e) => handleBinIdChange(e.target.value)}
            onBlur={onLocationBlur}
            onKeyPress={(e) => {
              // If user hits enter, clear the timer and trigger immediately
              if (e.key === 'Enter') {
                if (binDebounceTimerRef.current) {
                  clearTimeout(binDebounceTimerRef.current);
                  binDebounceTimerRef.current = null;
                }
                if (onLocationKeyPress) onLocationKeyPress(e);
              } else if (onLocationKeyPress) {
                onLocationKeyPress(e);
              }
            }}
            disabled={!prescriptionAssign}
            required
            error={Boolean(locationError)}
            helperText={locationError || ""}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isValidatingLocation ? (
                    <CircularProgress size={16} sx={{ color: "#7c3aed" }} />
                  ) : validatedLocation ? (
                    <CheckCircleIcon sx={{ color: "#10b981", fontSize: 20 }} />
                  ) : null}
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#ffffff",
                transition: "all 0.3s ease",
                fontSize: "0.875rem",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: locationError
                    ? "#ef4444"
                    : validatedLocation
                      ? "#10b981"
                      : "#e2e8f0",
                  borderWidth: 2,
                },
                "&:hover": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: locationError
                      ? "#ef4444"
                      : validatedLocation
                        ? "#10b981"
                        : "#7c3aed",
                  },
                },
                "&.Mui-focused": {
                  boxShadow: locationError
                    ? "0 0 0 3px rgba(239, 68, 68, 0.1)"
                    : validatedLocation
                      ? "0 0 0 3px rgba(16, 185, 129, 0.1)"
                      : "0 0 0 3px rgba(124, 58, 237, 0.1)",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: locationError
                      ? "#ef4444"
                      : validatedLocation
                        ? "#10b981"
                        : "#7c3aed",
                    borderWidth: 2,
                  },
                },
              },
              "& .MuiInputBase-input::placeholder": {
                fontSize: "0.875rem",
                color: "#9e9e9e",
                opacity: 1,
              },
              "& .MuiFormHelperText-root": {
                fontSize: "0.75rem",
                marginTop: "4px",
                marginLeft: 0,
                position: "absolute",
                top: "100%",
              },
            }}
          />
          )}
        </Grid>
        {showRoomNumber ? (
          <Grid item xs={12} sm={roomFieldWidth}>
            <TextField
              variant="outlined"
              placeholder={t("Assign.RoomNumber", { defaultValue: "Room number" })}
              value={localRoomNumber}
              onChange={(e) => handleRoomNumberChange(e.target.value)}
              disabled={!prescriptionAssign}
              fullWidth
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1.5, display: "flex", alignItems: "center" }}>
                    <MeetingRoomOutlinedIcon
                      sx={{
                        color: prescriptionAssign ? "#7c3aed" : "#b0bec5",
                        fontSize: 22,
                      }}
                    />
                  </Box>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  transition: "all 0.3s ease",
                  fontSize: "0.875rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e2e8f0",
                    borderWidth: 2,
                  },
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7c3aed",
                    },
                  },
                  "&.Mui-focused": {
                    boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.1)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7c3aed",
                      borderWidth: 2,
                    },
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "0.875rem",
                  color: "#9e9e9e",
                  opacity: 1,
                },
              }}
            />
          </Grid>
        ) : null}
        {showOrderNotes ? (
          <Grid item xs={12} sm={showRoomNumber ? 8 : 12}>
            <TextField
              variant="outlined"
              placeholder={t("Assign.BedsideOrderMessage", { defaultValue: "Message for delivery person" })}
              value={localOrderNotes}
              onChange={(e) => handleOrderNotesChange(e.target.value)}
              disabled={!prescriptionAssign}
              fullWidth
              helperText={t("Assign.BedsideOrderMessageHelper", {
                defaultValue: "Optional note shown when the order is opened.",
              })}
              InputProps={{
                inputProps: { maxLength: 500 },
                startAdornment: (
                  <Box sx={{ mr: 1.5, display: "flex", alignItems: "center" }}>
                    <ChatBubbleOutlineIcon
                      sx={{
                        color: prescriptionAssign ? "#7c3aed" : "#b0bec5",
                        fontSize: 22,
                      }}
                    />
                  </Box>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  transition: "all 0.3s ease",
                  fontSize: "0.875rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e2e8f0",
                    borderWidth: 2,
                  },
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7c3aed",
                    },
                  },
                  "&.Mui-focused": {
                    boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.1)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7c3aed",
                      borderWidth: 2,
                    },
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "0.875rem",
                  color: "#9e9e9e",
                  opacity: 1,
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "0.75rem",
                  marginTop: "4px",
                  marginLeft: 0,
                  color: "#94a3b8",
                },
              }}
            />
          </Grid>
        ) : null}
        {isEpicHasValidRx === false && isEpicHasValidRxMessage && (
          <Box sx={{ mt: 2, width: "100%", ml: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: "#ef4444", fontSize: "0.8rem" }}
            >
              {isEpicHasValidRxMessage}
            </Typography>
          </Box>
        )}
      </Grid>
    </Paper>
  );
};

ScanSection.propTypes = {
  t: PropTypes.func.isRequired,
  prescriptionAssign: PropTypes.bool,
  rxNumber: PropTypes.string,
  scanInputResetKey: PropTypes.number,
  onScanChange: PropTypes.func.isRequired,
  scanRxInputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  binId: PropTypes.string,
  onBinIdChange: PropTypes.func,
  onLocationBlur: PropTypes.func,
  onLocationKeyPress: PropTypes.func,
  locationError: PropTypes.string,
  isValidatingLocation: PropTypes.bool,
  validatedLocation: PropTypes.object,
  enableTrim: PropTypes.bool,
  trimChars: PropTypes.string,
  isEpicHasValidRx: PropTypes.bool,
  isEpicHasValidRxMessage: PropTypes.string,
  isNonRxDeliverEnabled: PropTypes.bool,
  otcDeliveryEntitlementLoading: PropTypes.bool,
  showNonRxScan: PropTypes.bool,
  upcScan: PropTypes.string,
  upcScanBusy: PropTypes.bool,
  onUpcScanChange: PropTypes.func,
  onUpcScanKeyDown: PropTypes.func,
  onUpcScanSubmit: PropTypes.func,
  showRoomNumber: PropTypes.bool,
  showLocationDropdown: PropTypes.bool,
  showOrderNotes: PropTypes.bool,
  roomNumber: PropTypes.string,
  onRoomNumberChange: PropTypes.func,
  orderNotes: PropTypes.string,
  onOrderNotesChange: PropTypes.func,
  onLocationSelect: PropTypes.func,
  onLocationOptionsOpen: PropTypes.func,
  locationOptions: PropTypes.array,
};

ScanSection.defaultProps = {
  prescriptionAssign: true,
  rxNumber: "",
  scanInputResetKey: 0,
  scanRxInputRef: null,
  binId: "",
  onBinIdChange: undefined,
  onLocationBlur: undefined,
  onLocationKeyPress: undefined,
  locationError: "",
  isValidatingLocation: false,
  validatedLocation: null,
  enableTrim: false,
  trimChars: SearchUtils.defaultTrimChars,
  isNonRxDeliverEnabled: false,
  otcDeliveryEntitlementLoading: false,
  showNonRxScan: false,
  upcScan: "",
  upcScanBusy: false,
  onUpcScanChange: undefined,
  onUpcScanKeyDown: undefined,
  onUpcScanSubmit: undefined,
  showRoomNumber: false,
  showLocationDropdown: undefined,
  showOrderNotes: undefined,
  roomNumber: "",
  onRoomNumberChange: undefined,
  orderNotes: "",
  onOrderNotesChange: undefined,
  onLocationSelect: undefined,
  onLocationOptionsOpen: undefined,
  locationOptions: [],
};

export default ScanSection;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/components/ScanSection.jsx